import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../Icon';
import api from '../../lib/api';
import { cmToFtIn, cmToIn, ftInToCm, inToCm, kgToLb, lbToKg, formatHeight, formatWeight, formatLength } from '../../lib/units';
import { panelCls, inputCls, labelCls, primaryBtn, ghostBtn, Field, SectionHead, Note, Stat } from './ui';
import { useHealthConsent, HealthConsentCheck } from './HealthConsent';

const SEX = [
  { v: '', l: 'Select…' },
  { v: 'female', l: 'Female' },
  { v: 'male', l: 'Male' },
  { v: 'prefer_not_to_say', l: 'Prefer not to say' },
];
const ACTIVITY = [
  { v: '', l: 'Select…' },
  { v: 'sedentary', l: 'Mostly sitting' },
  { v: 'light', l: 'Lightly active' },
  { v: 'moderate', l: 'Moderately active' },
  { v: 'active', l: 'Very active' },
  { v: 'very_active', l: 'Extremely active' },
];
const SLEEP_QUALITY = [
  { v: '', l: 'Select…' },
  { v: 'poor', l: 'Poor' },
  { v: 'fair', l: 'Fair' },
  { v: 'good', l: 'Good' },
  { v: 'great', l: 'Great' },
];
const SMOKING = [
  { v: '', l: 'Select…' },
  { v: 'never', l: 'Never smoked' },
  { v: 'former', l: 'Used to smoke' },
  { v: 'current', l: 'Currently smoke' },
];
const ALCOHOL = [
  { v: '', l: 'Select…' },
  { v: 'never', l: 'Never' },
  { v: 'rarely', l: 'Rarely' },
  { v: 'weekly', l: 'Weekly' },
  { v: 'several_times_week', l: 'Several times a week' },
  { v: 'daily', l: 'Daily' },
];
const DIET = [
  { v: '', l: 'Select…' },
  { v: 'mixed', l: 'Mixed / no particular pattern' },
  { v: 'mediterranean', l: 'Mediterranean style' },
  { v: 'vegetarian', l: 'Vegetarian' },
  { v: 'vegan', l: 'Vegan' },
  { v: 'low_carb', l: 'Lower carbohydrate' },
  { v: 'other', l: 'Something else' },
];

const Select = ({ id, value, onChange, options, testId }) => (
  <select id={id} data-testid={testId} value={value || ''} onChange={onChange} className={inputCls}>
    {options.map((o) => (
      <option key={o.v} value={o.v}>{o.l}</option>
    ))}
  </select>
);

export default function HealthMetricsPanel({ onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [system, setSystem] = useState('metric');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const consent = useHealthConsent();
  const [message, setMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    const { data: d } = await api.get('/me/health-metrics');
    setData(d);
    setForm(d.entered || {});
    const sys = d.entered?.unit_system || 'metric';
    setSystem(sys);
    const ft = cmToFtIn(d.entered?.height_cm ?? null);
    setHeightFt(ft.ft === '' ? '' : ft.ft);
    setHeightIn(ft.in === '' ? '' : ft.in);
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => {
    const raw = e.target.value;
    setForm((f) => ({ ...f, [k]: raw === '' ? null : raw }));
    setMessage('');
  };

  const setNum = (k, convert) => (e) => {
    const raw = e.target.value;
    setForm((f) => ({ ...f, [k]: raw === '' ? null : (convert ? convert(raw) : Number(raw)) }));
    setMessage('');
  };

  const imperial = system === 'imperial';

  const save = async () => {
    if (!(await consent.ensure())) {
      setMessage('Please tick the box above so we can store your health information.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const payload = { ...form, unit_system: system };
      if (imperial) payload.height_cm = ftInToCm(heightFt, heightIn);
      const { data: d } = await api.put('/me/health-metrics', payload);
      setData(d);
      setForm(d.entered || {});
      setMessage('Your health information has been saved.');
      if (onSaved) onSaved(d);
    } catch {
      setMessage('We couldn’t save that. Please check the values and try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete('/me/health-metrics');
      setConfirmDelete(false);
      setForm({});
      setHeightFt(''); setHeightIn('');
      await load();
      setMessage('Your health information has been deleted.');
    } finally {
      setBusy(false);
    }
  };

  const calc = data?.calculated;
  const weightValue = useMemo(() => {
    if (form.weight_kg == null) return '';
    return imperial ? kgToLb(form.weight_kg) : Math.round(form.weight_kg * 10) / 10;
  }, [form.weight_kg, imperial]);
  const waistValue = useMemo(() => {
    if (form.waist_cm == null) return '';
    return imperial ? cmToIn(form.waist_cm) : Math.round(form.waist_cm * 10) / 10;
  }, [form.waist_cm, imperial]);

  if (!data) {
    return <div className={panelCls} data-testid="metrics-loading"><p className="font-body-md text-body-md text-on-surface-variant">Loading your details…</p></div>;
  }

  return (
    <div className={`${panelCls} ph-no-capture`} data-testid="health-metrics-panel">
      <SectionHead eyebrow="Your Details" title="My Health Metrics">
        <div className="flex items-center gap-space-xs">
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">Units</span>
          {['metric', 'imperial'].map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`units-${s}`}
              onClick={() => setSystem(s)}
              className={`h-9 px-space-md rounded-full font-label-sm text-label-sm uppercase tracking-wider transition-colors ${system === s ? 'bg-surface-tint text-on-primary' : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'}`}
            >
              {s === 'metric' ? 'cm / kg' : 'ft / lb'}
            </button>
          ))}
        </div>
      </SectionHead>

      <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg max-w-2xl leading-relaxed">
        These few details let us personalise your guidance and work out your calculator results. Everything here is
        private to you, you can skip anything, and you can change or delete it whenever you like.
      </p>

      <div className="flex items-center gap-space-sm mb-space-lg">
        {[{ n: 1, l: 'Basics' }, { n: 2, l: 'More detail (optional)' }].map((s) => (
          <button
            key={s.n}
            type="button"
            data-testid={`metrics-step-${s.n}`}
            onClick={() => setStep(s.n)}
            className={`h-9 px-space-md rounded-full font-label-sm text-label-sm uppercase tracking-wider transition-colors ${step === s.n ? 'bg-outline text-on-primary' : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'}`}
          >
            {s.n}. {s.l}
          </button>
        ))}
      </div>

      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md" data-testid="metrics-basics">
          {imperial ? (
            <Field label="Height" htmlFor="h-ft">
              <div className="flex items-center gap-space-xs">
                <input id="h-ft" data-testid="metric-height-ft" type="number" min="1" max="8" className={inputCls} placeholder="ft" value={heightFt} onChange={(e) => setHeightFt(e.target.value)} />
                <input data-testid="metric-height-in" type="number" min="0" max="11.9" step="0.5" className={inputCls} placeholder="in" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} />
              </div>
            </Field>
          ) : (
            <Field label="Height (cm)" htmlFor="h-cm">
              <input id="h-cm" data-testid="metric-height" type="number" min="50" max="260" className={inputCls} placeholder="e.g. 172" value={form.height_cm ?? ''} onChange={setNum('height_cm')} />
            </Field>
          )}

          <Field label={imperial ? 'Weight (lb)' : 'Weight (kg)'} htmlFor="w">
            <input id="w" data-testid="metric-weight" type="number" step="0.1" className={inputCls} placeholder={imperial ? 'e.g. 154' : 'e.g. 70'} value={weightValue} onChange={setNum('weight_kg', imperial ? lbToKg : null)} />
          </Field>

          <Field label={imperial ? 'Waist (in)' : 'Waist (cm)'} hint="Measure around the narrowest part, above your hips." htmlFor="waist">
            <input id="waist" data-testid="metric-waist" type="number" step="0.1" className={inputCls} placeholder={imperial ? 'e.g. 32' : 'e.g. 82'} value={waistValue} onChange={setNum('waist_cm', imperial ? inToCm : null)} />
          </Field>

          <Field label="Age" htmlFor="age">
            <input id="age" data-testid="metric-age" type="number" min="13" max="120" className={inputCls} placeholder="e.g. 42" value={form.age ?? ''} onChange={setNum('age')} />
          </Field>

          <Field label="Sex" hint="Used only to make the estimates more accurate." htmlFor="sex">
            <Select id="sex" testId="metric-sex" value={form.sex} onChange={set('sex')} options={SEX} />
          </Field>

          <Field label="Activity level" htmlFor="activity">
            <Select id="activity" testId="metric-activity" value={form.activity_level} onChange={set('activity_level')} options={ACTIVITY} />
          </Field>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md" data-testid="metrics-advanced">
          <Field label="Resting heart rate (bpm)" htmlFor="rhr">
            <input id="rhr" data-testid="metric-rhr" type="number" className={inputCls} placeholder="e.g. 62" value={form.resting_heart_rate ?? ''} onChange={setNum('resting_heart_rate')} />
          </Field>
          <Field label="Blood pressure (systolic)" htmlFor="bps">
            <input id="bps" data-testid="metric-bp-sys" type="number" className={inputCls} placeholder="e.g. 118" value={form.blood_pressure_systolic ?? ''} onChange={setNum('blood_pressure_systolic')} />
          </Field>
          <Field label="Blood pressure (diastolic)" htmlFor="bpd">
            <input id="bpd" data-testid="metric-bp-dia" type="number" className={inputCls} placeholder="e.g. 76" value={form.blood_pressure_diastolic ?? ''} onChange={setNum('blood_pressure_diastolic')} />
          </Field>
          <Field label="Typical sleep (hours)" htmlFor="sh">
            <input id="sh" data-testid="metric-sleep-hours" type="number" step="0.5" className={inputCls} placeholder="e.g. 7.5" value={form.sleep_hours ?? ''} onChange={setNum('sleep_hours')} />
          </Field>
          <Field label="Sleep quality" htmlFor="sq">
            <Select id="sq" testId="metric-sleep-quality" value={form.sleep_quality} onChange={set('sleep_quality')} options={SLEEP_QUALITY} />
          </Field>
          <Field label="Daily steps" htmlFor="steps">
            <input id="steps" data-testid="metric-steps" type="number" className={inputCls} placeholder="e.g. 7000" value={form.daily_steps ?? ''} onChange={setNum('daily_steps')} />
          </Field>
          <Field label="Exercise days per week" htmlFor="edw">
            <input id="edw" data-testid="metric-exercise-days" type="number" min="0" max="7" className={inputCls} placeholder="e.g. 3" value={form.exercise_days_per_week ?? ''} onChange={setNum('exercise_days_per_week')} />
          </Field>
          <Field label="Minutes per session" htmlFor="eps">
            <input id="eps" data-testid="metric-exercise-minutes" type="number" className={inputCls} placeholder="e.g. 30" value={form.exercise_minutes_per_session ?? ''} onChange={setNum('exercise_minutes_per_session')} />
          </Field>
          <Field label="Smoking" htmlFor="smk">
            <Select id="smk" testId="metric-smoking" value={form.smoking_status} onChange={set('smoking_status')} options={SMOKING} />
          </Field>
          <Field label="Alcohol" htmlFor="alc">
            <Select id="alc" testId="metric-alcohol" value={form.alcohol_frequency} onChange={set('alcohol_frequency')} options={ALCOHOL} />
          </Field>
          <Field label="Eating pattern" htmlFor="diet">
            <Select id="diet" testId="metric-diet" value={form.dietary_pattern} onChange={set('dietary_pattern')} options={DIET} />
          </Field>
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Anything else you'd like to note" htmlFor="notes">
              <textarea id="notes" data-testid="metric-notes" rows={3} className={`${inputCls} h-auto py-space-sm resize-none`} placeholder="Optional" value={form.notes ?? ''} onChange={set('notes')} />
            </Field>
          </div>
        </div>
      )}

      <HealthConsentCheck granted={consent.granted} ticked={consent.ticked} setTicked={consent.setTicked} />

      <div className="flex flex-wrap items-center gap-space-md mt-space-lg pt-space-lg border-t border-surface-container">
        <button type="button" data-testid="metrics-save" onClick={save} disabled={busy || consent.blocked} className={primaryBtn}>
          <Icon name="save" className="text-[18px]" />
          <span>{busy ? 'Saving…' : 'Save my details'}</span>
        </button>
        {step === 1 && (
          <button type="button" data-testid="metrics-next" onClick={() => setStep(2)} className={ghostBtn}>Add more detail</button>
        )}
        {step === 2 && (
          <button type="button" data-testid="metrics-back" onClick={() => setStep(1)} className={ghostBtn}>Back to basics</button>
        )}
        {!confirmDelete ? (
          <button type="button" data-testid="metrics-delete" onClick={() => setConfirmDelete(true)} className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant hover:text-error transition-colors">
            Delete my health information
          </button>
        ) : (
          <span className="flex items-center gap-space-sm">
            <span className="font-body-sm text-body-sm text-on-surface-variant">Delete everything in this section?</span>
            <button type="button" data-testid="metrics-delete-confirm" onClick={remove} className="h-9 px-space-md rounded bg-error text-on-error font-label-sm text-label-sm uppercase tracking-wider">Delete</button>
            <button type="button" onClick={() => setConfirmDelete(false)} className="h-9 px-space-md rounded bg-surface-container text-outline font-label-sm text-label-sm uppercase tracking-wider">Cancel</button>
          </span>
        )}
        {message ? <span data-testid="metrics-message" className="font-body-sm text-body-sm text-primary font-semibold">{message}</span> : null}
      </div>

      {/* Calculated, not entered */}
      <div className="mt-space-xl">
        <div className="flex items-center gap-space-sm mb-space-md">
          <Icon name="functions" className="text-surface-tint text-[20px]" />
          <h4 className="font-title-md text-title-md uppercase tracking-wider text-outline">Worked out for you</h4>
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">(calculated, not entered)</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-md" data-testid="metrics-calculated">
          <Stat label="BMI" value={calc?.bmi ?? '—'} sub={calc?.bmi_band ? `This is ${calc.bmi_band}.` : 'Add height and weight.'} accent />
          <Stat label="Waist-to-height" value={calc?.waist_to_height_ratio ?? '—'} sub={calc?.waist_to_height_ratio ? 'Many guidelines suggest keeping this under 0.5.' : 'Add height and waist.'} />
          <Stat label="Height" value={formatHeight(form.height_cm ?? null, system)} sub="As you entered it." />
          <Stat label="Weight" value={formatWeight(form.weight_kg ?? null, system)} sub={`Waist: ${formatLength(form.waist_cm ?? null, system)}`} />
        </div>
        <div className="mt-space-md">
          <Note testId="metrics-disclaimer">
            These figures are estimates based on the numbers you entered. They are general information, not a medical
            assessment, and they don’t replace advice from your doctor.
          </Note>
        </div>
      </div>
    </div>
  );
}
